import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests';
import { LocalizationsBlock, validateLocalizations } from '../../features/LocalizationsBlock';
import { FormLayout, FormSection, FormField, FormLoading } from '../../shared/ui/FormLayout';
import { mapLocalizationsFromApi } from '../../shared/utils/buildEntityFormData.js';

const LOCALIZATION_FIELDS = [
  { name: 'title', label: 'Название', type: 'text', required: true },
  { name: 'description', label: 'Описание', type: 'textarea' },
];

const BACK_TO = '/events/categories';

const getErrorMessage = (error, fallback) =>
  error?.error || error?.response?.data?.error || error?.message || fallback;

/**
 * Категория мероприятий: название, описание и переводы — одним JSON-запросом
 */
const EventCategoryFormPage = () => {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState(isEdit ? null : { title: '', description: '' });
  const [localizations, setLocalizations] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiRequests.events
      .getCategory(id)
      .then((res) => {
        setForm({ id: res.id, title: res.title || '', description: res.description || '' });
        setLocalizations(mapLocalizationsFromApi(res.localizations, []));
      })
      .catch((error) => toast.error(getErrorMessage(error, 'Не удалось загрузить категорию')));
  }, [id]);

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form.title.trim()) {
      toast.error('Введите название категории');
      return;
    }
    const localizationsError = validateLocalizations(localizations, LOCALIZATION_FIELDS);
    if (localizationsError) {
      toast.error(localizationsError);
      return;
    }

    setSaving(true);
    try {
      const body = {
        ...form,
        localizations: localizations.map(
          ({ id: localizationId, language, title, description }) => ({
            id: localizationId,
            language,
            title,
            description,
          }),
        ),
      };
      if (isEdit) {
        await apiRequests.events.updateCategory(body);
        toast.success('Категория сохранена');
      } else {
        await apiRequests.events.createCategory(body);
        toast.success('Категория создана');
        navigate(BACK_TO);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Не удалось сохранить категорию'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <FormLoading />;

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate(BACK_TO)}
      saving={saving}
      submitText={isEdit ? 'Сохранить' : 'Создать'}
    >
      <FormSection title="Основная информация">
        <Row>
          <Col md={6}>
            <FormField label="Название" required>
              <Form.Control
                type="text"
                value={form.title}
                placeholder="Например, Конференции"
                onChange={(e) => setField('title', e.target.value)}
              />
            </FormField>
          </Col>
        </Row>
        <FormField label="Описание">
          <Form.Control
            as="textarea"
            rows={3}
            value={form.description}
            onChange={(e) => setField('description', e.target.value)}
          />
        </FormField>
      </FormSection>

      <LocalizationsBlock
        value={localizations}
        onChange={setLocalizations}
        fields={LOCALIZATION_FIELDS}
        source={form}
        aiContext="категория мероприятий"
      />
    </FormLayout>
  );
};

export default EventCategoryFormPage;
